import {
    Button,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Skeleton,
    TablePagination,
    TextField,
    Theme,
    ThemeProvider,
    Tooltip,
    Typography,
    createTheme,
    useMediaQuery
} from "@mui/material";
import React, { useState, useReducer, useEffect, Fragment } from "react";
import { PageHeader } from "../../components/PageHeader";
import { useStyles } from "../../common/appStyles";
import { IBasicAdInformation, ISponsoredAds, IUserDetail, IUserPaginationResponse } from "../../types/manage";
import { RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { PageEnum, Predicates, RoleEnum } from "../../types/enums";
import { showSnackbar } from "../../services/snackbar.service";
import { UserDetail } from "./UserDetail";
import { ChangeUserPassword, GetUserAdminList, UpdateUser, CreateUser } from "../../services/user-service";
import { ISortingExpressionRequest, sortingRequest } from "../../types/expression";
import { LoadingDialog } from "../../components/HelperComponents/LoadingDialog";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import EditIcon from '@mui/icons-material/Edit';
import { RedirectToDash } from "../RedirectToDash";
import { paginationExpression, Protoexpression } from "../../utilities/ProtoExpression";
import { updateSubmissionDialog } from "../../store/submissionDialogState";
import { ISubmissionState } from "../../types/youths";
import { BannerAd } from "../../components/main-layout/BannerAd";
/**
 * @remarks this page has a list of all users, their school, and region in a table of type {@link IUserDetail}. 
 * It can be searched through, and allows the user to select which column to search by.
 * The Create User, and Edit User buttons will open a dialog for creating or editing a user
 * named {@link UserDetail}
 * @returns an Html component with a table of all users.
*/
export function UserAdmin() {

    const classes = useStyles();
    const userRole = useSelector((state: RootState) => state.roles);
    const currentPage = userRole.userPageAccess[PageEnum.UserAdmin];
    const user = useSelector((state: RootState) => state.userDetail);
    const initialState = {
        page: 0,
        rowsPerPage: 10,
        noResult: false,
        titleText: "",
        pageTitle: "User Admin",
        loading: false,
        dropDown: "FirstName",
        showUserDialog: false,
        searchValue: "",
        count: 0,
        predId: 2
    };

    function reducer(state, action) {
        switch (action.type) {
            case 'SET_FIELD':
                return { ...state, [action.field]: action.value };
            default:
                throw new Error();
        }
    }

    const [state, dispatch] = useReducer(reducer, initialState);

    const setField = (field, value) => {
        dispatch({ type: 'SET_FIELD', field, value });
    }

    const [selectedUser, setSelectedUser] = useState<IUserDetail>();
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(state.searchValue);
    const [result, setResult] = useState<IUserPaginationResponse | undefined>(undefined);
    const [filteredRows, setFilteredRows] = useState<IUserDetail[]>([]);
    const [searchDisabled, setSearchDisabled] = useState(false);
    const [sortingBy, setSortingBy] = useState<sortingRequest>();
    const [currentSortModel, setCurrentSortModel] = useState([]);
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));
    const dispatcher = useDispatch();
    const submissionisOpenState = useSelector((state: RootState) => state.submissionDialog);

    const theme = createTheme({
        palette: {
            primary: {
                main: '#484A57'
            },
            secondary: {
                main: '#10AD61'
            },
            background: {
                default: '#ffffff'
            },
        },
        typography: {
            h6: {
                color: '#484A57'
            },
            h5: {
                color: '#10AD61'
            },
            body1: {
                color: 'black'
            }
        },
    });

    const columns: GridColDef[] = [
        {
            field: 'lastName',
            headerName: 'Name',
            width: 200,
            filterable: false,
            headerClassName: classes.DataHead,
            hideable: false,
            renderCell: (params) => (
                params.row.lastName + ", " + params.row.firstName
            ),
        },
        {
            field: 'school',
            headerName: 'School',
            headerClassName: classes.DataHead,
            width: 250,
            filterable: false,
            hideable: false,
        },
        {
            field: 'regions',
            headerName: 'Region',
            headerClassName: classes.DataHead,
            width: 270,
            filterable: false,
            hideable: false,
            sortable: false,
            renderCell: (params) => (
                parseRegions(params.row.regions)
            ),
        },
        {
            field: 'role',
            headerName: 'Role',
            headerClassName: classes.DataHead,
            width: 165,
            filterable: false,
            hideable: false,
        },
        {
            field: 'needsAction',
            headerName: 'Needs Action',
            headerClassName: classes.DataHead,
            width: 120,
            align: 'center',
            headerAlign: 'center',
            filterable: false,
            hideable: false,
            sortable: false,
        },
        {
            field: 'actions',
            headerName: 'Actions',
            headerClassName: classes.DataHead,
            width: 150,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            hideable: false,
            sortable: false,
            renderCell: (params) => (
                (currentPage.update && <strong>
                    <Tooltip title="Edit User" arrow disableInteractive
                        slotProps={{
                            popper: {
                                modifiers: [
                                    {
                                        name: 'offset',
                                        options: {
                                            offset: [0, -8],
                                        },
                                    },
                                ],
                            },
                        }}>
                        <Button
                            fullWidth
                            variant="outlined"
                            color="primary"
                            size="small"
                            onClick={(event) => {
                                onEditUser(params.row);
                            }}
                        >
                            <EditIcon className="fs20 mr6 icon" />
                            Edit User
                        </Button>
                    </Tooltip>
                </strong>)
            ),
        },
    ];

    const getUserAdminList = async (expression: ISortingExpressionRequest, region?) => {
        try {
            let response;
            response = region ? await GetUserAdminList(expression, region) : await GetUserAdminList(expression);
            setFilteredRows(response.userInformation);
            setField("count", response.count);
            setResult(response);
            checkNoFilteredResult(response.userInformation);
        } catch (error) {
            showSnackbar('Error getting data', 'error', 5000);
        }
    }

    const onEditUser = (row: any) => {
        setField("titleText", "Edit User");
        setSelectedUser(row);
        setField("showUserDialog", true);
    }

    const onAddNewUser = () => {
        setField("titleText", "Add User");
        setSelectedUser({
            userId: 0,
            firstName: "",
            lastName: "",
            email: "",
            grade: 0,
            school: "",
            schoolId: 0,
            regions: [],
            roleId: 0,
            role: "",
            needsAction: false,
            auth0UserId: '',
        });
        setField("showUserDialog", true);
    }

    const handleClose = () => {
        const current: ISubmissionState = { isOpen: true };
        dispatcher(updateSubmissionDialog(current));
        setField("showUserDialog", false);
    }

    const handleSave = async (requestType, savedUser) => {
        setField("loading", true);
        try {
            requestType === 'create' ? await CreateUser(savedUser) : await UpdateUser(savedUser);
            showSnackbar('Saved Successfully', 'success', 5000);
            setField("showUserDialog", false);
            if (requestType === 'create') {
                setField("page", 0);
                let expression: ISortingExpressionRequest = createExpression(state.dropDown, state.searchValue, state.predId, state.rowsPerPage, 0);
                if (state.dropDown === "Region") {
                    expression = createExpression("FirstName", "", state.predId, state.rowsPerPage, 0);
                    await getUserAdminList(expression, state.searchValue);
                }
                else {
                    await getUserAdminList(expression);
                }
            }
            else {
                let expression: ISortingExpressionRequest = createExpression(state.dropDown, state.searchValue, state.predId, state.rowsPerPage, state.page);
                if (state.dropDown === "Region") {
                    expression = createExpression("FirstName", "", state.predId, state.rowsPerPage, state.page);
                    await getUserAdminList(expression, state.searchValue);
                }
                else {
                    await getUserAdminList(expression);
                }
            }
        } catch (error: any) {
            showSnackbar(error, 'error', 8000);
        }
        finally {
            setField("loading", false);
            setField("searchValue", "");
            handleSearch("", "FirstName");
        }
    }

    const capitalizeFirst = (str) => {
        return str.charAt(0).toUpperCase() + str.slice(1);
    };

    const createSorting = (sortInfo?: sortingRequest) => {
        if (sortInfo) {
            sortInfo.fieldName = capitalizeFirst(sortInfo.fieldName);
            setSortingBy(
                {
                    fieldName: sortInfo.fieldName,
                    sort: sortInfo.sort
                }
            );
            return sortInfo;
        }
        else {
            setSortingBy(
                {
                    fieldName: "LastName",
                    sort: "asc"
                }
            );
            return {
                fieldName: "LastName",
                sort: "asc"
            };
        }
    }

    const createExpression = (fieldName, value, predicateId, entriesPerPage, pageNumber, sort?: sortingRequest) => {
        let expression;
        if (!sort && sortingBy) {
            sort = sortingBy;
        }
        if (predicateId === 2) {
            if (fieldName === "Region") {
                expression = createRegionExpression(fieldName, value, predicateId, entriesPerPage, pageNumber, sort);
            }
            else if (sort) {
                expression = {
                    userInfo: paginationExpression(fieldName, value, predicateId, entriesPerPage, pageNumber),
                    sorting: sort
                }
            }
            else {
                expression = {
                    userInfo: paginationExpression(fieldName, value, predicateId, entriesPerPage, pageNumber)
                }
            }
        }
        else {
            expression = createNeedsActionExpression(fieldName, value, predicateId, entriesPerPage, pageNumber, sort)
        }
        return expression;
    }

    const createRegionExpression = (fieldName, value, predicateId, entriesPerPage, pageNumber, sort?: sortingRequest) => {
        let expression =
        {
            userInfo: paginationExpression(fieldName, value, predicateId, entriesPerPage, pageNumber)
        }
        return expression;
    }

    const createNeedsActionExpression = (fieldName, value, predicateId, entriesPerPage, pageNumber, sort?: sortingRequest) => {
        let expression: ISortingExpressionRequest;
        if (sort) {
            expression = {
                userInfo: paginationExpression(fieldName, value, predicateId, entriesPerPage, pageNumber),
                sorting: sort
            }
        }
        else {
            expression = {
                userInfo: paginationExpression("NeedsAction", true, Predicates.Equals, entriesPerPage, pageNumber)
            }
        }
        return expression;
    }

    const handleChangePage = (event: unknown, newPage: number) => {
        setField("page", newPage);
        let expression: ISortingExpressionRequest = createExpression(state.dropDown, state.searchValue, state.predId, state.rowsPerPage, newPage);
        state.dropDown !== "Region" ? getUserAdminList(expression) : getUserAdminList(expression, state.searchValue);
    }

    const handleChangeRowsPerPage = (value) => {


        setField("rowsPerPage", +value);
        setField("page", 0)
        let expression: ISortingExpressionRequest = createExpression(state.dropDown, state.searchValue, state.predId, value, 0);
        state.dropDown !== "Region" ? getUserAdminList(expression) : getUserAdminList(expression, state.searchValue);

    }

    const handleSortModelChange = React.useCallback((sortModel, search, drop, perPage, p) => {
        setCurrentSortModel(sortModel);
        if (Array.isArray(sortModel) && sortModel.length !== 0) {
            const model: sortingRequest = {
                fieldName: sortModel[0].field,
                sort: sortModel[0].sort
            };
            getUserAdminList(createExpression(drop, search, state.predId, perPage, p, createSorting(model)));
        }
    }, []);

    const handleSearch = (value, option) => {
        if (option === "NeedsAction") {
            setField("predId", 1);
            setSearchDisabled(true);
            onSearch(true, option, 1);
        }
        else if (value === "") {
            setField("predId", 2);
            setSearchDisabled(false);
            onSearch(value, option, 2);
        }
    }

    const parseRegions = (RegionList) => {
        var list: string;
        list = '';
        RegionList.forEach(element => {
            list = list + element.name + ", "
        });
        list = list.replace(/, $/, '');
        return list;
    }

    useEffect(() => {
        const timerId = setTimeout(() => {
            setDebouncedSearchQuery(state.searchValue);
        }, 500);
        return () => clearTimeout(timerId);
    }, [state.searchValue]);

    useEffect(() => {
        if (debouncedSearchQuery) {
            onSearch(debouncedSearchQuery, state.dropDown, state.predId);
        }
    }, [debouncedSearchQuery]);

    const onSearch = (searchQuery, option: string, predicateId) => {
        setField("page", 0);
        const expression: ISortingExpressionRequest = createExpression(option, searchQuery, predicateId, state.rowsPerPage, 0);
        if (option !== "Region") {
            getUserAdminList(expression);
        } else {
            getUserAdminList(expression, searchQuery);
        }
    };

    const checkNoFilteredResult = (check) => {
        setField("noResult", check.length === 0 ? true : false);
    }

    const handleChangePassword = async (request: string) => {
        try {
            await ChangeUserPassword(request);
            showSnackbar('Email Sent Successfully', 'success', 5000);
        }
        catch (error) {
            showSnackbar('Error Sending Email', 'error', 5000);
        }
    }

    useEffect(() => {
        if (currentPage?.read) {
            const expression: ISortingExpressionRequest =
            {
                userInfo: {
                    expressionRequest: {
                        protoexpressions: [
                            Protoexpression("FirstName", "", Predicates.StartsWith)
                        ]
                    },
                    paginationRequest: {
                        entriesPerPage: 10,
                        pageNumber: 1
                    }
                }
            }
            getUserAdminList(expression);
        }
    }, [currentPage, user]);

    return (
        <Fragment>
            <ThemeProvider theme={theme}>
                <Grid
                    container
                    className={classes.formContainer}
                    spacing={1}
                    justifyContent="center"
                    alignItems={'center'}
                >
                    {user.roleId >= RoleEnum.CredentialingManager && !lgUp && <Grid className={classes.bannerTopPadding}>
                        <BannerAd />
                    </Grid>}
                    <Grid item sm={12} className='justify-center' padding="5px">
                        {currentPage?.read &&
                            <Grid
                                container
                                spacing={1}
                                justifyContent="center"
                                alignItems="center"
                                className={classes.fixGridMargin}
                            >
                                <Grid item xs={2}><PageHeader title={state.pageTitle} onBackClick={undefined} /></Grid>
                                <><Grid item xs={12}>
                                    <Grid container justifyContent={"center"} alignItems={"center"} spacing={1} direction={{ xs: 'column', md: 'row' }}>
                                        <Grid item xs={2} md={2}>
                                            {currentPage?.create &&
                                                <Button variant="contained" size="large" aria-labelledby="add new user" onClick={() => onAddNewUser()} fullWidth>
                                                    Add New User
                                                </Button>}
                                        </Grid>
                                        <Grid item xs={4} md={0}>
                                            <FormControl variant='outlined' fullWidth>
                                                <TextField
                                                    type="text"
                                                    label="Search Table"
                                                    aria-labelledby="Search textfield"
                                                    value={state.searchValue}
                                                    onChange={(e) => {
                                                        setField("searchValue", e.target.value);
                                                        handleSearch(e.target.value, state.dropDown);
                                                    }}
                                                    disabled={searchDisabled}
                                                >
                                                </TextField>
                                            </FormControl>
                                        </Grid>
                                        <Grid item xs={1.5}>
                                            <FormControl variant="outlined" fullWidth>
                                                <InputLabel>Search By</InputLabel>
                                                <Select
                                                    label="Search By"
                                                    defaultValue="firstName"
                                                    aria-labelledby="search sort dropdown"
                                                    value={state.dropDown}
                                                    onChange={(values) => {
                                                        setField("dropDown", values.target.value);
                                                        handleSearch("", values.target.value);
                                                        setField("searchValue", "");
                                                    }}
                                                >
                                                    <MenuItem value={"FirstName"} aria-labelledby="first name">First Name</MenuItem>
                                                    <MenuItem value={"LastName"} aria-labelledby="last name">Last Name</MenuItem>
                                                    <MenuItem value={"School"} aria-labelledby="school">School</MenuItem>
                                                    <MenuItem value={"Region"} aria-labelledby="region">Region</MenuItem>
                                                    <MenuItem value={"Role"} aria-labelledby="role">Role</MenuItem>
                                                    <MenuItem value={"NeedsAction"} aria-labelledby="needs action">Needs Action</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    </Grid>
                                </Grid>
                                    <Grid
                                        container
                                        justifyContent={'center'}
                                        alignItems={"center"}
                                        spacing={1}
                                        className={classes.gridPaddingTop}
                                    >
                                        <Grid item sm={11} className={classes.tableGrid} maxWidth={{ xs: '45vh', sm: '100%', md: '100%' }}>
                                            {(result !== undefined && currentPage) ? (
                                                state.noResult || <Paper elevation={3}>
                                                    <Grid>
                                                        <div>
                                                            <DataGrid
                                                                rows={filteredRows}
                                                                columns={columns}
                                                                getRowHeight={() => 'auto'}
                                                                getEstimatedRowHeight={() => 100}
                                                                sx={{ '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '10px' }, }}
                                                                getRowId={(row) => row.id}
                                                                disableRowSelectionOnClick={true}
                                                                disableMultipleRowSelection
                                                                sortingMode="server"
                                                                sortModel={currentSortModel}
                                                                onSortModelChange={event => handleSortModelChange(event, state.searchValue, state.dropDown, state.rowsPerPage, state.page)}
                                                                hideFooter={true}
                                                                style={{ height: "61vh" }}
                                                            />
                                                        </div>
                                                        <TablePagination
                                                            rowsPerPageOptions={[10, 25, 50]}
                                                            component="div"
                                                            count={state.count}
                                                            rowsPerPage={state.rowsPerPage}
                                                            page={state.page}
                                                            onPageChange={handleChangePage}
                                                            onRowsPerPageChange={(event) => handleChangeRowsPerPage(event.target.value)} />
                                                    </Grid>
                                                </Paper>)
                                                :
                                                <Grid container direction={"column"} rowSpacing={.5}>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rectangular' width={"100%"} height={"3vh"} />
                                                    </Grid>
                                                    <Grid item xs={12}>
                                                        <Skeleton variant='rounded' width={'100%'} height={'54vh'} />
                                                    </Grid>
                                                </Grid>
                                            }
                                        </Grid>
                                        <Grid alignSelf={"center"} className={classes.tableNoResult}>
                                            {state.noResult && <FormControl fullWidth><Typography variant="h5">No Results Found</Typography></FormControl>}
                                        </Grid>
                                    </Grid></>
                                {currentPage?.read && <Grid item sm={12}>
                                    {state.showUserDialog && <UserDetail
                                        showDialog={state.showUserDialog}
                                        selectedUser={selectedUser}
                                        titleText={state.titleText}
                                        handleClose={handleClose}
                                        handleSave={handleSave}
                                        handleChangePassword={handleChangePassword}
                                    />}
                                </Grid>}
                            </Grid>
                        }
                        {!currentPage?.read && currentPage?.read !== undefined &&
                            <RedirectToDash />
                        }
                    </Grid>
                    {state.loading && <LoadingDialog
                        loading={state.loading}
                    />}
                </Grid>
            </ThemeProvider>
        </Fragment>
    );
}
